import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EditBadgeModal from "./EditBadgeModal";
import {
  BadgeListAction,
  DeleteBadgeAction,
} from "../../Redux/Actions/RewardAction";

const BadgesTab = () => {
  const dispatch = useDispatch<any>();
  const { data: badgeData, loading } = useSelector(
    (state: any) => state.BadgeListData
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const badges = badgeData?.rows || [];
  const totalCount = badgeData?.count || 0;

  const refreshBadgeList = () => {
    const param = `?page=${page}&page_size=${rowsPerPage}`;
    dispatch(BadgeListAction(param));
  };

  useEffect(() => {
    refreshBadgeList();
  }, [dispatch, page, rowsPerPage]);

  const handleEdit = (badge: any) => {
    setSelectedBadge(badge);
    setEditModalOpen(true);
  };

  const handleDelete = async (badgeId: string) => {
    if (window.confirm("Are you sure you want to delete this badge?")) {
      const result = await dispatch(DeleteBadgeAction(badgeId));
      if (result && result.status === true) {
        refreshBadgeList();
      }
    }
  };

  const calculateTotalPages = (count: number, pageSize: number) => {
    return Math.ceil(count / pageSize);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 1000 }}>
        <Table aria-label="Badges Table">
          <TableHead sx={{ backgroundColor: "#0555c8" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Image</TableCell>
              <TableCell sx={{ color: "white" }}>Name</TableCell>
              <TableCell sx={{ color: "white" }}>Code</TableCell>
              <TableCell sx={{ color: "white" }}>Level</TableCell>
              <TableCell sx={{ color: "white" }}>Value</TableCell>
              <TableCell sx={{ color: "white" }}>Description</TableCell>
              <TableCell sx={{ color: "white" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {badges?.length > 0 ? (
              badges?.map((b: any) => (
                <TableRow key={b?.id}>
                  <TableCell>
                    {b?.image_url ? (
                      <img
                        src={b?.image_url}
                        alt={b?.name}
                        style={{ width: 40, height: 40, borderRadius: "4px" }}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>{b?.name}</TableCell>
                  <TableCell>{b?.code}</TableCell>
                  <TableCell>{b?.level}</TableCell>
                  <TableCell>{b?.criteria_json?.value || "-"}</TableCell>
                  <TableCell>{b?.description}</TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(b)}
                        sx={{ color: "#0555c8" }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(b.id)}
                        sx={{ color: "#d32f2f" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No Badges Found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ m: 2, display: "flex", justifyContent: "center" }}>
        <Stack spacing={2}>
          <Pagination
            count={calculateTotalPages(totalCount, rowsPerPage)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      </Box>

      {editModalOpen && selectedBadge && (
        <EditBadgeModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedBadge(null);
          }}
          badge={selectedBadge}
          onSuccess={refreshBadgeList}
        />
      )}
    </>
  );
};

export default BadgesTab;
